import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';  // Import Provider from react-redux
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, Grid, Typography, Stack, Button, Divider } from '@mui/material';

import styles from './App.module.css';
import theme from './theme';
import store from './store';  // Import the Redux store
import Nav from './Nav';
import Home from './Home';
import About from './About';
import Cart from './Cart';
import Terms from './Terms';
import Checkout, { PAYPAL } from './Checkout';
import Confirmation from './Confirmation';
import Support from './Support';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ProductsPage from './ProductsPage';
import ProductShowPage from './ProductShowPage';
import { CatalogProvider } from './contexts/CatalogContext.jsx';
import { CartProvider } from './contexts/CartContext.jsx';

const App = () => {
  const navLinks = [
    { text: 'Home', path: '/' },
    { text: 'Products', path: '/products' },
    { text: 'How It Works', scrollTo: 'calendar' },
    { text: 'Support', path: '/support' },
    { text: 'About', path: '/about' },
    { text: 'Cart', path: '/cart', endIcon: <ShoppingCartIcon sx={{ fontSize: '1.3rem !important' }} /> },
  ];

  const ctaLink = { text: 'Order Now', path: '/products' };

  const checkoutProps = {
    provider: PAYPAL,
    providerProps: {}
  }

  return (
    <div className={styles.app}>
      <Nav brand="D.I.Y. Casino Party" navLinks={navLinks} cta={ctaLink} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:slug" element={<ProductShowPage />} />
	<Route path="/cart" element={<Cart />} />
	<Route path="/terms" element={<Terms />} />
	<Route path="/checkout" element={<Checkout {...checkoutProps} />} />
	<Route path="/confirmation" element={<Confirmation />} />
	<Route path="/support" element={<Support />} />
      </Routes>
      <Box component="footer" sx={{ background: 'linear-gradient(135deg, #01311f, #001b12)', color: '#fff', mt: 8, pt: 6, pb: 3, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" sx={{ fontFamily: '"Playfair Display", serif' }}>DIY Casino Party</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 1 }}>
                Premium table cloth rentals shipped nationwide with concierge-level support.
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>support@diycasinoparty.com</Typography>
              <Typography variant="body2">(404) 555-0142</Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>Company</Typography>
              <Stack spacing={0.5}>
                <a href="/about" style={{ color: 'inherit', textDecoration: 'none' }}>About</a>
                <a href="/support" style={{ color: 'inherit', textDecoration: 'none' }}>Support</a>
                <a href="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</a>
                <a href="/cart" style={{ color: 'inherit', textDecoration: 'none' }}>Cart</a>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>Stay in touch</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 1 }}>
                Need help choosing dates or games? Drop us a note and we'll respond same day.
              </Typography>
              <Button variant="contained" color="secondary" size="small" href="/support" sx={{ borderRadius: 999 }}>Contact Support</Button>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>Follow</Typography>
              <Stack spacing={0.5}>
                <a href="https://www.instagram.com" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>Instagram</a>
                <a href="https://www.facebook.com" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>Facebook</a>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 3 }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            © {new Date().getFullYear()} DIY Casino Party. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </div>
  );
}

ReactDOM.render(
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CatalogProvider>
        <CartProvider>
          <Router>
            <App />
          </Router>
        </CartProvider>
      </CatalogProvider>
    </ThemeProvider>
  </Provider>,
  document.getElementById('root')
);
